import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  UsersRound, Plus, Loader2, ShieldAlert, UserCheck, Target, TrendingUp,
  AlertTriangle, ChevronRight, Activity,
} from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { succLabel, riskTone, planTone, auditLine } from '@/lib/successionLabels';

const DONUT_COLORS = { in_development: '#2b7fa5', ready: '#2e9e77', no_successor: '#d9a13b', not_started: '#8a97a3' };

function Kpi({ icon: Icon, value, label, testId }) {
  return (
    <Card className="p-5 shadow-card flex items-center gap-4" data-testid={testId}>
      <span className="h-11 w-11 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5" />
      </span>
      <div className="min-w-0">
        <p className="text-2xl font-bold leading-tight">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </Card>
  );
}

function HBar({ label, value, max, tone = 'bg-primary' }) {
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-24 shrink-0 text-muted-foreground text-xs">{label}</span>
      <div className="flex-1 h-3 rounded bg-muted overflow-hidden">
        <div className={`h-full rounded ${tone}`} style={{ width: `${max ? (value / max) * 100 : 0}%` }} />
      </div>
      <span className="w-6 text-right font-semibold tabular-nums text-xs">{value}</span>
    </div>
  );
}

export default function ClientSuccession() {
  const { t, i18n } = useTranslation();
  const isES = !(i18n.language || '').toLowerCase().startsWith('en');
  const x = (es, en) => (isES ? es : en);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ position_title: '', department_name: '', incumbent_user_id: '', incumbent_name: '', vacant: false, criticality: 'medium', risk: 'medium', horizon: 'undetermined', business_impact: '', notes: '' });
  const [employees, setEmployees] = useState([]);

  const load = useCallback(() => {
    api.get('/client/succession/overview').then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => {
    load();
    api.get('/client/employees').then((r) => setEmployees(r.data?.items || r.data || [])).catch(() => {});
  }, [load]);

  const create = async () => {
    if (!form.position_title.trim() || (!form.vacant && !form.incumbent_name.trim())) {
      toast.error(x('Posición y titular actual (o estado Vacante) son obligatorios.', 'Position and current incumbent (or Vacant state) are required.'));
      return;
    }
    setSaving(true);
    try {
      const { data: p } = await api.post('/client/succession/positions', form);
      toast.success(x('Posición crítica creada.', 'Critical position created.'));
      setOpen(false);
      navigate(`/client/succession/${p.id}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  if (!data) {
    return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }
  const k = data.kpis;
  const dist = data.plan_status_distribution || {};
  const donut = Object.entries(dist).filter(([, v]) => v > 0).map(([kk, v]) => ({ name: kk, value: v }));
  const critMax = Math.max(1, ...Object.values(data.by_criticality || {}));
  const riskMax = Math.max(1, ...Object.values(data.by_risk || {}));

  return (
    <div className="min-h-screen" data-testid="client-succession">
      <Topbar
        title="Succession"
        subtitle={x('Asegurando la continuidad del talento crítico', 'Securing critical-talent continuity')}
        actions={(
          <Button onClick={() => setOpen(true)} className="bg-gradient-primary hover:opacity-90 gap-1.5" data-testid="succ-add-position-btn">
            <Plus className="h-4 w-4" /> {x('Nueva posición crítica', 'New critical position')}
          </Button>
        )}
      />
      <div className="p-6 lg:p-8 space-y-6">

      <Card className="relative overflow-hidden border-0 shadow-elegant" data-testid="succ-hero">
        <div className="bg-[#0f3d5c] px-8 py-8 lg:py-10 text-white flex items-center justify-between gap-6">
          <div className="max-w-xl">
            <h2 className="font-display text-lg md:text-lg font-bold leading-snug">{x('Personas, roles y oportunidades para un futuro más fuerte', 'People, roles and opportunities for a stronger future')}</h2>
            <p className="text-sm text-white/70 mt-2">{x('Identifica, desarrolla y prepara el talento que asegurará la continuidad de tu organización.', 'Identify, develop and prepare the talent that will secure your organization\u2019s continuity.')}</p>
          </div>
          <p className="hidden lg:block text-right text-base italic text-white/85 shrink-0 border-r-2 border-[#7FDCDC] pr-4">
            {x('"El liderazgo de hoy construye el mañana."', '"Today\u2019s leadership builds tomorrow."')}
          </p>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi icon={Target} value={k.critical_positions} label={x('Posiciones críticas', 'Critical positions')} testId="succ-kpi-positions" />
        <Kpi icon={UserCheck} value={k.successors_identified} label={x('Sucesores únicos (personas)', 'Unique successors (people)')} testId="succ-kpi-successors" />
        <Kpi icon={TrendingUp} value={k.coverage_pct === null ? '—' : `${k.coverage_pct}%`} label={x('Cobertura de sucesión', 'Succession coverage')} testId="succ-kpi-coverage" />
        <Kpi icon={Activity} value={k.plans_in_development} label={x('Planes en desarrollo', 'Plans in development')} testId="succ-kpi-indev" />
      </div>

      {(k.reviews_overdue > 0 || k.targets_reached > 0 || k.readiness_gaps > 0) && (
        <Card className="p-4 shadow-card border-warning/40 bg-warning/5" data-testid="succ-alerts">
          <p className="text-sm font-semibold flex items-center gap-2 text-warning">
            <AlertTriangle className="h-4 w-4" /> {x('Alertas de gobierno de preparación', 'Readiness governance alerts')}
          </p>
          <div className="mt-2 space-y-1 text-sm text-foreground/85">
            {(data.alerts?.review_overdue || []).map((a, i) => (
              <p key={`ro${i}`} data-testid="succ-alert-review">{x('Revisión de preparación vencida', 'Readiness review overdue')}: <b>{a.successor}</b> · {a.position_title} · {a.next_review_date}</p>
            ))}
            {(data.alerts?.targets_reached || []).map((a, i) => (
              <p key={`tr${i}`} data-testid="succ-alert-target">{x('El horizonte de preparación esperado se ha alcanzado y requiere revisión', 'The expected readiness horizon has been reached and requires review')}: <b>{a.successor}</b> · {a.position_title}</p>
            ))}
            {(data.alerts?.readiness_gaps || []).map((a, i) => (
              <p key={`rg${i}`} data-testid="succ-alert-gap">{x('Brecha de preparación', 'Readiness gap')}: <b>{a.position_title}</b> ({succLabel(isES, 'horizon', a.horizon)})</p>
            ))}
          </div>
        </Card>
      )}

      {data.positions.length === 0 ? (
        <Card className="p-12 shadow-card text-center space-y-3" data-testid="succ-empty-state">
          <UsersRound className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <h3 className="text-base font-semibold">{x('No hay posiciones críticas configuradas todavía.', 'No critical positions configured yet.')}</h3>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {x('Identifica las posiciones que requieren planificación de sucesión para comenzar a gestionar la continuidad de tu organización.',
               'Identify the positions that require succession planning to start managing your organization\u2019s continuity.')}
          </p>
          <Button onClick={() => setOpen(true)} className="bg-gradient-primary hover:opacity-90 gap-1.5" data-testid="succ-empty-cta">
            <Plus className="h-4 w-4" /> {x('Crear primera posición crítica', 'Create first critical position')}
          </Button>
        </Card>
      ) : (
      <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="p-5 shadow-card space-y-3" data-testid="succ-chart-criticality">
          <h4 className="text-sm font-semibold">{x('Posiciones críticas por criticidad', 'Critical positions by criticality')}</h4>
          {['high', 'medium', 'low'].map((lvl) => (
            <HBar key={lvl} label={succLabel(isES, 'criticality', lvl)} value={data.by_criticality[lvl] || 0} max={critMax}
              tone={lvl === 'high' ? 'bg-[#0f3d5c]' : lvl === 'medium' ? 'bg-[#2b7fa5]' : 'bg-[#7FDCDC]'} />
          ))}
        </Card>
        <Card className="p-5 shadow-card" data-testid="succ-chart-plans">
          <h4 className="text-sm font-semibold mb-2">{x('Estado de los planes de sucesión', 'Succession-plan status')}</h4>
          <div className="flex items-center gap-4">
            <div className="h-32 w-32 relative shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={donut.length ? donut : [{ name: 'empty', value: 1 }]} dataKey="value" innerRadius={38} outerRadius={56} strokeWidth={0}>
                    {(donut.length ? donut : [{ name: 'empty' }]).map((d, i) => (
                      <Cell key={i} fill={DONUT_COLORS[d.name] || '#e4e9ee'} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xl font-bold">{k.critical_positions}</span>
                <span className="text-[10px] text-muted-foreground">{x('planes', 'plans')}</span>
              </div>
            </div>
            <div className="space-y-1.5 text-xs">
              {Object.entries(dist).map(([kk, v]) => (
                <div key={kk} className="flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: DONUT_COLORS[kk] || '#e4e9ee' }} />
                  <span className="font-semibold tabular-nums w-4">{v}</span>
                  <span className="text-muted-foreground">{succLabel(isES, 'plan', kk)}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
        <Card className="p-5 shadow-card space-y-3" data-testid="succ-chart-risk">
          <h4 className="text-sm font-semibold">{x('Riesgo de vacancia (posiciones críticas)', 'Vacancy risk (critical positions)')}</h4>
          {['high', 'medium', 'low'].map((lvl) => (
            <HBar key={lvl} label={succLabel(isES, 'risk', lvl)} value={data.by_risk[lvl] || 0} max={riskMax}
              tone={lvl === 'high' ? 'bg-destructive' : lvl === 'medium' ? 'bg-warning' : 'bg-success'} />
          ))}
        </Card>
      </div>

      {(data.concentration_risks || []).length > 0 && (
        <Card className="p-4 shadow-card border-warning/40 bg-warning/5" data-testid="succ-concentration-risk">
          <p className="text-sm font-semibold flex items-center gap-2 text-warning">
            <AlertTriangle className="h-4 w-4" /> {x('Riesgo de concentración de sucesores', 'Successor concentration risk')}
          </p>
          <div className="mt-2 space-y-1 text-sm">
            {data.concentration_risks.map((c, i) => (
              <p key={i} className="text-foreground/85">
                <b>{c.name}</b> — {x(`sucesor/a potencial de ${c.count} posiciones críticas:`, `potential successor for ${c.count} critical positions:`)} {c.positions.join(' · ')}
              </p>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">{x('Varias posiciones dependen de la misma persona: la cobertura no es independiente.', 'Several positions rely on the same person: coverage is not independent.')}</p>
        </Card>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <Card className="xl:col-span-2 shadow-card overflow-hidden" data-testid="succ-positions-table">
          <div className="p-4 border-b flex items-center justify-between">
            <h4 className="text-sm font-semibold">{x('Posiciones críticas', 'Critical positions')}</h4>
          </div>
          {data.positions.length === 0 ? (
            <p className="p-8 text-sm text-muted-foreground text-center" data-testid="succ-positions-empty">
              {x('Aún no hay posiciones críticas. Crea la primera para comenzar la planificación de sucesión.', 'No critical positions yet. Create the first one to begin succession planning.')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="border-b bg-muted/40 text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-4 py-2.5 font-medium">{x('Posición', 'Position')}</th>
                    <th className="px-4 py-2.5 font-medium">{x('Criticidad', 'Criticality')}</th>
                    <th className="px-4 py-2.5 font-medium">{x('Riesgo', 'Risk')}</th>
                    <th className="px-4 py-2.5 font-medium">{x('Cobertura', 'Coverage')}</th>
                    <th className="px-4 py-2.5 font-medium">{x('Estado del plan', 'Plan status')}</th>
                    <th className="px-2 py-2.5" />
                  </tr>
                </thead>
                <tbody>
                  {data.positions.map((p) => (
                    <tr key={p.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer transition-smooth"
                      onClick={() => navigate(`/client/succession/${p.id}`)} data-testid={`succ-position-row-${p.id}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium">{p.position_title}</p>
                        <p className="text-xs text-muted-foreground">{p.incumbent_name}{p.department_name ? ` · ${p.department_name}` : ''}</p>
                      </td>
                      <td className="px-4 py-3">{succLabel(isES, 'criticality', p.criticality)}</td>
                      <td className="px-4 py-3"><Badge variant="outline" className={riskTone[p.risk]}>{succLabel(isES, 'risk', p.risk)}</Badge></td>
                      <td className="px-4 py-3">
                        {p.successor_count > 0
                          ? x(`${p.successor_count} sucesor${p.successor_count > 1 ? 'es' : ''}`, `${p.successor_count} successor${p.successor_count > 1 ? 's' : ''}`)
                          : <span className="text-destructive font-medium">{x('Sin sucesor', 'No successor')}</span>}
                      </td>
                      <td className="px-4 py-3"><Badge variant="outline" className={planTone[p.plan_status]}>{succLabel(isES, 'plan', p.plan_status)}</Badge></td>
                      <td className="px-2 py-3"><ChevronRight className="h-4 w-4 text-muted-foreground" /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card className="p-4 shadow-card" data-testid="succ-activity">
          <h4 className="text-sm font-semibold mb-3">{x('Actividad reciente', 'Recent activity')}</h4>
          {(data.recent_activity || []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{x('Sin actividad todavía.', 'No activity yet.')}</p>
          ) : data.recent_activity.map((a, i) => (
            <div key={i} className="flex items-start gap-2.5 py-2 border-b last:border-0">
              <span className="h-7 w-7 rounded-lg bg-primary/10 text-primary inline-flex items-center justify-center shrink-0 mt-0.5"><ShieldAlert className="h-3.5 w-3.5" /></span>
              <div className="min-w-0">
                <p className="text-sm leading-snug">{auditLine(isES, a)}</p>
                <p className="text-xs text-muted-foreground truncate">{a.entity_name} · {a.actor_name} · {(a.timestamp || '').slice(0, 10)}</p>
              </div>
            </div>
          ))}
        </Card>
      </div>
      </>
      )}

      <p className="text-[11px] text-muted-foreground" data-testid="succ-governance-note">
        {x('La designación como sucesor potencial no constituye una promoción, nombramiento ni garantía de empleo. Las decisiones permanecen bajo juicio humano autorizado. Información confidencial de RR. HH.',
           'A potential-successor nomination is not a promotion, appointment or employment guarantee. Decisions remain subject to authorized human judgment. Confidential HR information.')}
      </p>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-[900px]" data-testid="succ-create-dialog">
          <DialogHeader><DialogTitle>{x('Nueva posición crítica', 'New critical position')}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2 space-y-1.5">
              <Label>{x('Posición', 'Position')}</Label>
              <Input data-testid="succ-form-title" value={form.position_title} onChange={(e) => setForm({ ...form, position_title: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{x('Departamento', 'Department')}</Label>
              <Input data-testid="succ-form-dept" value={form.department_name} onChange={(e) => setForm({ ...form, department_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{x('Titular actual', 'Current incumbent')}</Label>
              <Select value={form.vacant ? '__vacant__' : (form.incumbent_user_id || '')}
                onValueChange={(v) => {
                  if (v === '__vacant__') setForm({ ...form, vacant: true, incumbent_user_id: '', incumbent_name: '' });
                  else {
                    const u = employees.find((emp) => emp.id === v);
                    setForm({ ...form, vacant: false, incumbent_user_id: v, incumbent_name: u?.name || '', department_name: form.department_name || u?.department_name || '' });
                  }
                }}>
                <SelectTrigger data-testid="succ-form-incumbent"><SelectValue placeholder={x('Seleccionar…', 'Select…')} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__vacant__">{x('Vacante / Sin titular', 'Vacant / No incumbent')}</SelectItem>
                  {employees.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {[['criticality', x('Criticidad', 'Criticality'), ['high', 'medium', 'low']],
              ['risk', x('Riesgo de sucesión', 'Succession risk'), ['high', 'medium', 'low']],
              ['horizon', x('Horizonte esperado', 'Expected horizon'), ['0_1y', '1_2y', '2y_plus', 'undetermined']]].map(([field, label, opts]) => (
              <div key={field} className="space-y-1.5">
                <Label>{label}</Label>
                <Select value={form[field]} onValueChange={(v) => setForm({ ...form, [field]: v })}>
                  <SelectTrigger data-testid={`succ-form-${field}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {opts.map((o) => <SelectItem key={o} value={o}>{succLabel(isES, field === 'horizon' ? 'horizon' : field, o)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
            <div className="sm:col-span-2 space-y-1.5">
              <Label>{x('Impacto en el negocio', 'Business impact')}</Label>
              <Textarea data-testid="succ-form-impact" rows={6} className="min-h-[9rem] max-h-[40vh] leading-relaxed" value={form.business_impact} onChange={(e) => setForm({ ...form, business_impact: e.target.value })} />
              <p className="text-[11px] text-muted-foreground">
                {x('Describe el impacto operativo, financiero, regulatorio o estratégico si la posición queda sin cobertura.',
                   'Describe the operational, financial, regulatory or strategic impact if the position is left uncovered.')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{x('Cancelar', 'Cancel')}</Button>
            <Button onClick={create} disabled={saving} className="bg-gradient-primary hover:opacity-90" data-testid="succ-form-submit">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : x('Crear posición', 'Create position')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
}
